import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { authApi } from '@/lib/api';
import { TermsContent } from '@/components/TermsContent';
import { LEGAL_DOCS } from '@/constants/terms';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function TermsModal() {
    const { user, refreshAuth } = useAuth();
    const [isAccepting, setIsAccepting] = useState(false);

    // Solo requerir aceptación para administradores de gimnasio (gym_admin)
    if (!user || user.role !== 'gym_admin' || user.terms_accepted) return null;

    const handleAccept = async () => {
        setIsAccepting(true);
        try {
            await authApi.acceptTerms(
                user.user_id,
                user.gym_id,
                LEGAL_DOCS.terms.version,
                'dashboard_modal'
            );
            await refreshAuth();
            toast.success('Términos aceptados correctamente');
        } catch (error) {
            console.error('Error accepting terms:', error);
            toast.error('Error al aceptar los términos. Intenta de nuevo.');
        } finally {
            setIsAccepting(false);
        }
    };

    return (
        <Dialog open={true}>
            <DialogContent
                className="sm:max-w-[600px] max-h-[90vh] flex flex-col p-0 overflow-hidden border-none shadow-2xl glass"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <div className="p-6 pb-0 shrink-0">
                    <DialogHeader className="flex flex-row items-center gap-4 space-y-0">
                        <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                            <ShieldCheck className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-black">Términos y Condiciones</DialogTitle>
                            <DialogDescription className="text-sm font-medium">
                                Por favor, revisa y acepta nuestros términos para continuar usando el sistema.
                            </DialogDescription>
                        </div>
                    </DialogHeader>
                </div>

                <div className="flex-1 overflow-y-auto p-6 min-h-0 scrollbar-thin scrollbar-thumb-orange-500/20 scrollbar-track-transparent">
                    <TermsContent variant="modal" />
                </div>

                <DialogFooter className="p-6 bg-slate-50/50 dark:bg-white/5 border-t border-slate-100 dark:border-white/10">
                    <Button
                        className="w-full sm:w-auto font-bold gradient-primary shadow-lg h-12 px-8 rounded-xl"
                        onClick={handleAccept}
                        disabled={isAccepting}
                    >
                        {isAccepting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Procesando...
                            </>
                        ) : (
                            'Acepto los Términos y Condiciones'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
